import { DownloadIcon, Trash2Icon } from "lucide-react";

import { useI18n } from "~/i18n";
import { cn } from "~/lib/utils";
import { Spinner } from "../ui/spinner";
import type { VaultItem } from "../../morrowVault";
import { VaultCardPreview } from "./VaultCardPreview";
import { VaultFileFolderMenu } from "./VaultFileFolderMenu";
import { VAULT_ITEM_DRAG_TYPE } from "./VaultFolderCard";
import { MorrowPersonAvatar } from "../workspace/MorrowPersonCard";
import { formatBytes, resolveVaultMimeType, typeLabel } from "./vaultShared";
import type { CustomVaultFolder } from "./vaultFolders";

export function VaultFileGrid({
  items,
  addedByLabel,
  customFolders,
  itemFolderAssignments,
  deletingIds,
  onOpen,
  onDownload,
  onDelete,
  onAssignFolder,
}: {
  items: readonly VaultItem[];
  addedByLabel: string;
  customFolders: readonly CustomVaultFolder[];
  itemFolderAssignments: Readonly<Record<string, string>>;
  deletingIds: ReadonlySet<string>;
  onOpen: (item: VaultItem) => void;
  onDownload: (item: VaultItem) => void;
  onDelete: (item: VaultItem) => void;
  onAssignFolder: (itemId: string, folderId: string | null) => void;
}) {
  const { t } = useI18n();

  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-border/60 bg-card px-6 py-10 text-center text-sm text-muted-foreground">
        {t("vault.folderEmpty")}
      </div>
    );
  }

  return (
    <ul className="grid grid-cols-3 gap-3">
      {items.map((item) => {
        const mimeType = resolveVaultMimeType(item);
        const deleting = deletingIds.has(item.id);

        return (
          <li key={item.id}>
            <article
              // Draggable onto a folder card to file it — the same gesture the list view has had,
              // carrying the same custom type so the card can tell a vault file apart from an
              // operating-system file drop. Grid is the DEFAULT view, so without this the whole
              // drag-into-a-folder feature was invisible unless you first switched to the list.
              // The overflow menu stays as the keyboard-reachable route.
              draggable
              onDragStart={(event) => {
                event.dataTransfer.setData(VAULT_ITEM_DRAG_TYPE, item.id);
                event.dataTransfer.effectAllowed = "move";
              }}
              className={cn(
                "group flex flex-col overflow-hidden rounded-xl border border-border/60 bg-card transition-colors hover:border-border hover:bg-accent/10",
                deleting && "opacity-60",
              )}
            >
              <div className="relative">
                <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-end gap-0.5 p-1.5 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
                  <div className="pointer-events-auto flex items-center gap-0.5 rounded-md border border-border/50 bg-card/95 p-0.5 shadow-sm backdrop-blur-sm">
                    <VaultFileFolderMenu
                      itemName={item.name}
                      customFolders={customFolders}
                      assignedFolderId={itemFolderAssignments[item.id]}
                      onAssign={(folderId) => onAssignFolder(item.id, folderId)}
                      className="size-6"
                      iconClassName="size-3"
                    />
                    <button
                      type="button"
                      aria-label={`${t("vault.download")} ${item.name}`}
                      disabled={deleting}
                      onClick={(event) => {
                        event.stopPropagation();
                        onDownload(item);
                      }}
                      className="flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <DownloadIcon className="size-3" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete ${item.name}`}
                      disabled={deleting}
                      onClick={(event) => {
                        event.stopPropagation();
                        onDelete(item);
                      }}
                      className="flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-destructive"
                    >
                      {deleting ? (
                        <Spinner className="size-3" />
                      ) : (
                        <Trash2Icon className="size-3" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => onOpen(item)}
                  className="relative block w-full overflow-hidden border-b border-border/40 bg-muted/20"
                >
                  <div className="relative aspect-[8/5] w-full">
                    <div className="absolute inset-0 min-h-0 min-w-0">
                      <VaultCardPreview item={item} />
                    </div>
                  </div>
                </button>
              </div>

              <div className="flex items-center justify-between gap-2 p-2">
                <div className="flex min-w-0 max-w-[48%] shrink-0 items-center gap-1.5">
                  <MorrowPersonAvatar
                    name={addedByLabel}
                    size="sm"
                    className="rounded-md bg-primary/10 text-primary"
                  />
                  <span
                    className="min-w-0 truncate text-[11px] text-muted-foreground"
                    title={addedByLabel}
                  >
                    {addedByLabel}
                  </span>
                </div>

                <div className="min-w-0 flex-1 text-right">
                  <p className="truncate text-xs font-medium text-foreground" title={item.name}>
                    {item.name}
                  </p>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {typeLabel(mimeType)} · {formatBytes(item.sizeBytes)}
                  </p>
                </div>
              </div>
            </article>
          </li>
        );
      })}
    </ul>
  );
}
